import { useFindReplaceStates } from '@/atoms/findReplaceAtoms';
import { snippetModalAtom } from '@/components/modal/snippet-editor-modal-atoms';
import { renderHtmlToMarkdown } from '@/components/tiptap/markdown-native/canonical-renderer';
import { useSectionEditorContext } from '@/components/tiptap/markdown-native/editor-type';
import { trackSectionCreated } from '@/components/tiptap/markdown-native/section-node/section-analytics';
import { convertSelectionToSection } from '@/components/tiptap/shared/convert-selection-to-section';
import { copyMarkdownNativeSelectionWithNoteSettings } from '@/components/tiptap/shared/copy-selection';
import { stripEmptySectionHeadingPresentationHtml } from '@/components/tiptap/shared/section-markers';
import {
  openSnippetFromSelection,
  seedFindReplaceFromSelection,
} from '@/components/tiptap/shared/text-menu-selection-commands';
import { useCopySettings } from '@/features/copySettings';
import { copySettingsToWire } from '@/features/copySettings/useCopySettings.helpers';
import { isSelectionCopySettingsEnabled } from '@/features/flags/selection-copy-settings';
import {
  COPY_SUPERSEDED,
  isFormattingOff,
  writeHtmlAndMarkdownToClipboard,
} from '@/helpers/copyHelpers/copyHelpers-v2';
import { sessionCopyClicked } from '@/lib/analytics/scribe/session';
import { toast } from '@heidi/design-system/toast';
import { type CopyFormattingLoss } from '@heidi/editor/core';
// Presentation-only HTML normalization for external paste targets (Word/Epic/
// Outlook): collapses paragraph breaks to <br/>, unwraps the <p> inside each
// <li>, and inlines table borders (all of which live in CSS and get stripped
// once the fragment leaves the page). Pure HTML string transforms — not the
// markdown↔HTML serializer. Mirrors the vMarkdown note twin for paste parity.
import { isSupersededCopyError } from '@/helpers/copyHelpers/reportCopyFailure';
import { reformatHtml } from '@/helpers/markdownToHtml';
import { getSelectionWordCounts } from '@/helpers/wordCount';
import { CopyMethod, SessionCopiedFormat } from '@/models/analytics/features/sessions';
import { logger } from '@/utils/logger/logger';
import { getCurrentSessionId } from '@heidi/runtime/shell';
import { getHTMLFromFragment, type Editor } from '@tiptap/react';
import { useSetAtom } from 'jotai';
import { useCallback } from 'react';
import { useIntl } from 'react-intl';

/**
 * Bubble-menu commands the markdown-native editor shares with the vMarkdown note
 * menu — a twin of `vMarkdown/menus/text-menu/hooks/useTextmenuCommands` for the
 * features ported here (copy, find & replace, create snippet). No vMarkdown
 * imports: copy serializes through the markdown-native pipeline
 * (`renderHtmlToMarkdown`) rather than the legacy regex serializers, and find &
 * replace / snippet reach the shared session-level modals via their atoms.
 */
export const useMarkdownNativeTextMenuCommands = (editor: Editor) => {
  const intl = useIntl();
  const { onFindReplaceClick } = useFindReplaceStates();
  const setSnippetModalOpen = useSetAtom(snippetModalAtom);
  const editorContext = useSectionEditorContext();
  const { settings, isImprovedCopyEnabled } = useCopySettings();

  const onCopy = useCallback(async () => {
    try {
      const { from, to } = editor.state.selection;
      if (from === to) {
        toast({
          tone: 'destructive',
          title: intl.formatMessage({
            defaultMessage: 'No text selected',
            id: 'niEXfG/YKw',
          }),
        });
        return;
      }
      // ProseMirror → HTML for `text/html`; the same HTML back through the
      // markdown-native pipeline for the `text/plain` markdown sibling.
      const selectedHtml = getHTMLFromFragment(
        editor.state.doc.slice(from, to).content,
        editor.schema
      );
      const presentationHtml = stripEmptySectionHeadingPresentationHtml(selectedHtml);
      const honourSettings = isSelectionCopySettingsEnabled() && isImprovedCopyEnabled;
      // Measured only on the toggle-honouring path; the verbatim path below
      // applies no settings, so it has no formatting loss to report.
      let formattingLoss: CopyFormattingLoss | undefined;
      if (honourSettings) {
        const outcome = await copyMarkdownNativeSelectionWithNoteSettings(
          presentationHtml,
          settings
        );
        // A newer copy replaced this one before it was published: it is that
        // copy's event to send, so this attempt emits no `session_copied`.
        if (outcome === COPY_SUPERSEDED) return;
        formattingLoss = outcome;
      } else {
        await writeHtmlAndMarkdownToClipboard({
          html: reformatHtml(presentationHtml),
          markdown: renderHtmlToMarkdown(presentationHtml),
        });
      }

      const { selectedWordCount, totalWordCount } = getSelectionWordCounts(
        editor.state.doc,
        from,
        to
      );
      sessionCopyClicked({
        format: honourSettings
          ? SessionCopiedFormat.WITH_TOGGLES
          : SessionCopiedFormat.FORMAT_WITH_LISTS,
        copyMethod: CopyMethod.CLICK,
        selectedWordCount,
        totalWordCount,
        editorType: 'markdown_native',
        copySource: 'pane_copy_button',
        htmlFlavorWritten: honourSettings ? !isFormattingOff(settings) : true,
        ...(honourSettings && { copySettings: copySettingsToWire(settings) }),
        ...(formattingLoss != null && { formattingLoss }),
      });

      toast({
        tone: 'success',
        title: intl.formatMessage({
          defaultMessage: 'Copied to clipboard',
          id: '93oVThAHvU',
        }),
      });
    } catch (error) {
      // The desktop form of supersession: a newer copy landed, so this one is
      // a silent cancellation like `COPY_SUPERSEDED` above.
      if (isSupersededCopyError(error)) return;
      // PHI-safe: session id, editor variant and error name only — never the
      // clipboard contents.
      logger.error('[markdown-native] bubble-menu copy failed', {
        session_id: getCurrentSessionId(),
        editor_type: 'markdown_native',
        error_name: error instanceof Error ? error.name : 'unknown',
      });
      toast({
        tone: 'destructive',
        title: intl.formatMessage({
          defaultMessage: 'Failed to copy',
          id: '8Iv00QJneF',
        }),
      });
    }
  }, [editor, intl, isImprovedCopyEnabled, settings]);

  const onFindReplace = useCallback(
    () => seedFindReplaceFromSelection(editor, onFindReplaceClick),
    [editor, onFindReplaceClick]
  );

  const onCreateSnippet = useCallback(
    () => openSnippetFromSelection(editor, setSnippetModalOpen),
    [editor, setSnippetModalOpen]
  );

  const onConvertToSection = useCallback(() => {
    const converted = convertSelectionToSection(editor);
    if (!converted) {
      toast({
        tone: 'warning',
        title: intl.formatMessage({
          defaultMessage: "This selection can't be converted into a section",
          id: 'GZp0BbPvBB',
        }),
      });
      return;
    }
    trackSectionCreated(editorContext, 'convert_selection');
  }, [editor, intl, editorContext]);

  return { onCopy, onFindReplace, onCreateSnippet, onConvertToSection };
};
